import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import notification from 'antd/lib/notification';

import { t } from 'utils/i18n';
import { SAVING_OVERLAY_CLASS } from 'utils/mousetrap-react';
import { findDrawingModels, findModels } from './references/references-api';
import type { StateLike } from './references/reference-model';
import './references/styles.scss';

export interface RelabelFillProps {
    states: StateLike[]; // the objects of the current frame
    collect(frame: number): Promise<StateLike[]>; // read after a save: new shapes have their server ids then
    save(): Promise<void>; // throws when changes stay unsaved
    find(serverIDs: number[]): Promise<void>;
    reload(): Promise<void>; // re-reads annotations from the server
}

// a photo shape is searched by its own crop, a drawing shape takes the models picked on the photos
export function finderFor(jobId: number, photoTaskId: number | null): (serverIDs: number[]) => Promise<void> {
    if (photoTaskId !== null) return async (ids) => { await findModels(jobId, photoTaskId, ids); };
    return (ids) => findDrawingModels(jobId, ids);
}

function hasModelSpec(state: StateLike): boolean {
    return state.objectType === 'shape' && state.label.attributes.some((spec) => spec.name === 'blend_model');
}

function message(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

export function RelabelFill(props: RelabelFillProps): JSX.Element | null {
    const {
        states, collect, save, find, reload,
    } = props;
    const [busy, setBusy] = useState<string | null>(null);
    const labels = useRef(new Map<number, number>());
    const queue = useRef<Promise<void>>(Promise.resolve());
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const fill = useCallback(async (state: StateLike): Promise<void> => {
        if (!mounted.current) return;
        setBusy(state.label.name);
        let sent = false;
        try {
            await save();
            const fresh = (await collect(state.frame)).find((item) => item.clientID === state.clientID);
            if (fresh?.serverID) {
                sent = true;
                await find([fresh.serverID]);
            }
        } catch (caught) {
            notification.error({ message: t('Could not find models: {error}', { error: message(caught) }) });
        } finally {
            // a reload drops the edits made meanwhile, so the overlay stays until it is done
            if (sent && mounted.current) await reload().catch(() => undefined);
            if (mounted.current) setBusy(null);
        }
    }, [collect, save, find, reload]);

    // the same object with another label: a reload or another frame brings other clientIDs
    useEffect(() => {
        const changed = states.filter((state) => {
            const before = labels.current.get(state.clientID);
            return before !== undefined && before !== state.label.id && hasModelSpec(state);
        });
        labels.current = new Map(states.map((state) => [state.clientID, state.label.id]));
        for (const state of changed) {
            queue.current = queue.current.then(() => fill(state));
        }
    }, [states, fill]);

    if (busy === null) return null;
    return (
        <div className={`${SAVING_OVERLAY_CLASS} cvat-references-busy`}>
            <span className='cvat-references-busy-text'>{t('Finding models for {label}…', { label: busy })}</span>
        </div>
    );
}
